import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  retry,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '../index';
import { nanoid } from 'nanoid';
import {
  errorHandler,
  ErrorCategory,
  ErrorSeverity,
} from '@/lib/error-handling';

// Configuration for different adapter modes
const getApiBaseUrl = () => {
  // For now, always use remote API, but this will support local adapters in the future
  const useRemoteApi = true; // Will be configurable based on user preference

  if (useRemoteApi) {
    // Check for centralized web configuration
    const webConfig = (window as any).__PHRASEWRIGHT_CONFIG__;

    if (webConfig?.urls?.api) {
      return webConfig.urls.api;
    }

    // Use relative paths in production, explicit URL in development
    return import.meta.env.PROD
      ? '' // Production API endpoint (relative paths work)
      : import.meta.env.VITE_API_URL || 'http://localhost:6003'; // Development API endpoint
  }

  // Future: return local adapter endpoint
  return '/api/local';
};

// Wrapper to fix URL and add error handling
const baseQueryWithUrlFix: BaseQueryFn = retry(
  async (args, api, extraOptions) => {
    // If args is a string with trailing ?, remove it
    if (typeof args === 'string' && args.endsWith('?')) {
      args = args.slice(0, -1);
    } else if (typeof args === 'object' && args.url && args.url.endsWith('?')) {
      args = { ...args, url: args.url.slice(0, -1) };
    }

    // Clean up empty params to prevent trailing ?
    if (typeof args === 'object' && args.params) {
      const cleanParams: Record<string, any> = {};
      Object.entries(args.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          cleanParams[key] = value;
        }
      });
      // If no params remain, remove params entirely
      if (Object.keys(cleanParams).length === 0) {
        const { params, ...rest } = args;
        args = rest;
      } else {
        args = { ...args, params: cleanParams };
      }
    }

    const baseQuery = fetchBaseQuery({
      baseUrl: getApiBaseUrl(),
      prepareHeaders: (headers, { getState }) => {
        const state = getState() as RootState;

        // Add content type
        headers.set('Content-Type', 'application/json');

        // Add auth headers if available
        // const token = state.auth?.token;
        // if (token) {
        //   headers.set('Authorization', `Bearer ${token}`);
        // }

        // Add project context if available
        const projectId = state.project?.currentProject?.id;
        if (projectId) {
          headers.set('X-Project-ID', projectId);
        }

        // Add client metadata for debugging
        headers.set('X-Client-Type', 'web');
        headers.set('X-Client-Version', '0.0.0');

        // Add request ID for tracing
        headers.set('X-Request-ID', nanoid());

        return headers;
      },
    });

    const result = await baseQuery(args, api, extraOptions);

    // Handle errors
    if (result.error) {
      const status = typeof result.error.status === 'number' 
        ? result.error.status 
        : parseInt(result.error.status as string, 10);
      const category =
        status === 401 || status === 403
          ? ErrorCategory.AUTH
          : status === 422 || status === 400
            ? ErrorCategory.VALIDATION
            : status >= 500
              ? ErrorCategory.SYSTEM
              : ErrorCategory.NETWORK;

      const severity =
        status >= 500
          ? ErrorSeverity.HIGH
          : status === 401
            ? ErrorSeverity.MEDIUM
            : ErrorSeverity.LOW;

      errorHandler.handleError(
        new Error(`API Error: ${result.error.data || status}`),
        {
          category,
          severity,
          metadata: {
            status,
            url: typeof args === 'string' ? args : args.url,
            method: typeof args === 'object' ? args.method : 'GET',
          },
        }
      );
    }

    return result;
  },
  {
    maxRetries: 3,
  }
);

// Define the base API slice with adapter pattern support
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithUrlFix,

  // Enhanced tag types for better cache invalidation
  tagTypes: [
    'File', // Individual files
    'Files', // File collections
    'Project', // Project metadata
    'ProjectFiles', // Project-scoped file lists
    'Revision', // File revisions
    'RevisionList', // Revision collections
    'Search', // Search results
    'Stats', // File statistics
    'Tags', // Tag collections
    'Reference', // @ reference relationships
    'Export', // Export operations
    'Import', // Import operations
  ],

  // Keep connections alive for better performance
  keepUnusedDataFor: 60, // 1 minute

  // Refetch on network reconnection
  refetchOnReconnect: true,

  // Don't refetch on focus by default (can be overridden per endpoint)
  refetchOnFocus: false,

  // Enhanced error handling
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === 'persist/REHYDRATE' && action.payload) {
      return (action.payload as any)[reducerPath];
    }
  },

  endpoints: () => ({}),
});

// Export utilities for adapter pattern
export const getApiConfig = () => ({
  baseUrl: getApiBaseUrl(),
  isRemoteApi: getApiBaseUrl().includes('http'),
  isLocalAdapter: !getApiBaseUrl().includes('http'),
});

// Cache invalidation helpers
export const invalidateTags = {
  file: (id: string) => [{ type: 'File' as const, id }],
  files: () => [{ type: 'Files' as const }],
  project: (id: string) => [{ type: 'Project' as const, id }],
  projectFiles: (projectId: string) => [
    { type: 'ProjectFiles' as const, id: projectId },
  ],
  revision: (fileId: string, revisionId: string) => [
    { type: 'Revision' as const, id: `${fileId}:${revisionId}` },
  ],
  revisionList: (fileId: string) => [
    { type: 'RevisionList' as const, id: fileId },
  ],
  search: (query: string) => [{ type: 'Search' as const, id: query }],
  tags: () => [{ type: 'Tags' as const }],
  references: (fileId: string) => [{ type: 'Reference' as const, id: fileId }],
};

// Provider tags helpers
export const provideTags = {
  file: (id: string) => [{ type: 'File' as const, id }],
  files: (ids: string[]) => [
    { type: 'Files' as const },
    ...ids.map((id) => ({ type: 'File' as const, id })),
  ],
  project: (id: string) => [{ type: 'Project' as const, id }],
  projectFiles: (projectId: string, fileIds: string[]) => [
    { type: 'ProjectFiles' as const, id: projectId },
    ...fileIds.map((id) => ({ type: 'File' as const, id })),
  ],
  revision: (fileId: string, revisionId: string) => [
    { type: 'Revision' as const, id: `${fileId}:${revisionId}` },
  ],
  revisionList: (fileId: string, revisionIds: string[]) => [
    { type: 'RevisionList' as const, id: fileId },
    ...revisionIds.map((id) => ({
      type: 'Revision' as const,
      id: `${fileId}:${id}`,
    })),
  ],
  search: (query: string, fileIds: string[]) => [
    { type: 'Search' as const, id: query },
    ...fileIds.map((id) => ({ type: 'File' as const, id })),
  ],
  tags: (tags: string[]) => [
    { type: 'Tags' as const },
    ...tags.map((tag) => ({ type: 'Tags' as const, id: tag })),
  ],
  references: (fileId: string, referencedIds: string[]) => [
    { type: 'Reference' as const, id: fileId },
    ...referencedIds.map((id) => ({ type: 'Reference' as const, id })),
  ],
};

export default baseApi;
